import { ApiError } from '../lib/apiClient';
import { uploadVoiceCapture } from './captureService';
import type { AnalysisMode, CaptureDto } from '../types/capture';

/**
 * Offline queue of voice notes (docs/voiceFeature.md, UC-6). A note that cannot
 * be uploaded right away (no connection, upload failed) is kept in IndexedDB on
 * this device and uploaded one after the other once the app is online again —
 * only while the app is open (no service worker). The original recording time
 * travels with it; the queue item id is the upload's Idempotency-Key, so a
 * retried upload never creates a second note. Cleared on logout (privacy D16).
 */

export type QueuedNoteStatus = 'waiting' | 'failed';

export interface QueuedNote {
  id: string;
  userId: string;
  aquariumId: string;
  blob: Blob;
  mimeType: string;
  recordedAt: string;
  durationMs: number;
  language?: string;
  deferAnalysis?: boolean;
  analysisMode?: AnalysisMode;
  attempts: number;
  /** Error code of the last failed upload (e.g. 'NETWORK', 'VALIDATION_ERROR'). */
  lastError: string | null;
  status: QueuedNoteStatus;
  /** Epoch ms before which no automatic retry happens (backoff). */
  nextAttemptAt: number;
}

/** Persistence of the queue — IndexedDB in the app, in memory in tests. */
export interface VoiceQueueStore {
  list(): Promise<QueuedNote[]>;
  put(note: QueuedNote): Promise<void>;
  remove(id: string): Promise<void>;
  /** Removes the notes of one user, or every note when userId is omitted. */
  clear(userId?: string): Promise<void>;
}

const DB_NAME = 'reefguide-voice';
const STORE = 'voice-queue';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE, { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'));
    request.onblocked = () => reject(new Error('IndexedDB blocked'));
  });
}

function run<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  return openDb().then(
    (db) =>
      new Promise<T | undefined>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const request = fn(tx.objectStore(STORE));
        tx.oncomplete = () => {
          db.close();
          resolve(request ? request.result : undefined);
        };
        tx.onerror = () => {
          db.close();
          reject(tx.error ?? new Error('IndexedDB transaction failed'));
        };
        tx.onabort = () => {
          db.close();
          reject(tx.error ?? new Error('IndexedDB transaction aborted'));
        };
      }),
  );
}

export function createIndexedDbStore(): VoiceQueueStore {
  return {
    list: async () => ((await run<QueuedNote[]>('readonly', (s) => s.getAll())) ?? []),
    put: async (note) => {
      await run('readwrite', (s) => s.put(note));
    },
    remove: async (id) => {
      await run('readwrite', (s) => s.delete(id));
    },
    clear: async (userId) => {
      if (userId === undefined) {
        await run('readwrite', (s) => s.clear());
        return;
      }
      const notes = (await run<QueuedNote[]>('readonly', (s) => s.getAll())) ?? [];
      await run('readwrite', (s) => {
        notes.filter((n) => n.userId === userId).forEach((n) => s.delete(n.id));
      });
    },
  };
}

export function createMemoryStore(initial: QueuedNote[] = []): VoiceQueueStore {
  const notes = new Map(initial.map((n) => [n.id, n]));
  return {
    list: async () => [...notes.values()],
    put: async (note) => {
      notes.set(note.id, note);
    },
    remove: async (id) => {
      notes.delete(id);
    },
    clear: async (userId) => {
      for (const [id, note] of notes) if (userId === undefined || note.userId === userId) notes.delete(id);
    },
  };
}

/**
 * True when recordings can be stored on this device (false e.g. in some
 * private browsing modes) — then notes are uploaded immediately only.
 */
export async function storageAvailable(): Promise<boolean> {
  try {
    await run('readonly', (s) => s.count());
    return true;
  } catch {
    return false;
  }
}

/** Removes every queued note of this device (logout, privacy D16). Never throws. */
export async function clearVoiceQueue(store: VoiceQueueStore = createIndexedDbStore()): Promise<void> {
  try {
    await store.clear();
  } catch {
    // nothing stored / storage unavailable
  }
}

export async function clearQueueForUser(userId: string, store: VoiceQueueStore = createIndexedDbStore()): Promise<void> {
  try {
    await store.clear(userId);
  } catch {
    // nothing stored / storage unavailable
  }
}

export const MIN_BACKOFF_MS = 5_000;
export const MAX_BACKOFF_MS = 5 * 60_000;

export function backoffMs(attempts: number): number {
  return Math.min(MAX_BACKOFF_MS, MIN_BACKOFF_MS * 2 ** Math.max(0, attempts - 1));
}

/** True for errors that a later retry can fix (no connection, server trouble, rate limit). */
function isTransient(err: unknown): boolean {
  if (!(err instanceof ApiError)) return true;
  return err.status >= 500 || err.status === 429 || err.status === 408 || err.status === 0;
}

export type Uploader = (note: QueuedNote) => Promise<CaptureDto>;

export const defaultUploader: Uploader = (note) =>
  uploadVoiceCapture({
    aquariumId: note.aquariumId,
    audio: note.blob,
    mimeType: note.mimeType,
    recordedAt: note.recordedAt,
    durationMs: note.durationMs,
    language: note.language,
    deferAnalysis: note.deferAnalysis,
    analysisMode: note.analysisMode,
    idempotencyKey: note.id,
  });

export interface VoiceQueueOptions {
  userId: string;
  store?: VoiceQueueStore;
  upload?: Uploader;
  onUploaded?: (capture: CaptureDto, note: QueuedNote) => void;
  now?: () => number;
  /** Attempts before a note with a server error is marked failed (no connection never fails a note). */
  maxServerAttempts?: number;
}

/** The uploader of one user's queue. Sequential (concurrency 1). */
export class VoiceQueue {
  private readonly store: VoiceQueueStore;
  private readonly upload: Uploader;
  private readonly now: () => number;
  private readonly listeners = new Set<(notes: QueuedNote[]) => void>();
  private processing: Promise<void> | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private detach: (() => void) | null = null;

  constructor(private readonly options: VoiceQueueOptions) {
    this.store = options.store ?? createIndexedDbStore();
    this.upload = options.upload ?? defaultUploader;
    this.now = options.now ?? Date.now;
  }

  async list(): Promise<QueuedNote[]> {
    try {
      const notes = await this.store.list();
      return notes
        .filter((n) => n.userId === this.options.userId)
        .sort((a, b) => a.recordedAt.localeCompare(b.recordedAt));
    } catch {
      return [];
    }
  }

  subscribe(listener: (notes: QueuedNote[]) => void): () => void {
    this.listeners.add(listener);
    void this.list().then(listener);
    return () => this.listeners.delete(listener);
  }

  private async emit(): Promise<void> {
    const notes = await this.list();
    this.listeners.forEach((l) => l(notes));
  }

  async enqueue(input: Omit<QueuedNote, 'userId' | 'attempts' | 'lastError' | 'status' | 'nextAttemptAt'> & { lastError?: string | null }): Promise<QueuedNote> {
    const note: QueuedNote = {
      ...input,
      userId: this.options.userId,
      attempts: 0,
      lastError: input.lastError ?? null,
      status: 'waiting',
      nextAttemptAt: this.now() + MIN_BACKOFF_MS,
    };
    await this.store.put(note);
    await this.emit();
    this.schedule();
    return note;
  }

  async discard(id: string): Promise<void> {
    await this.store.remove(id);
    await this.emit();
  }

  /** Uploads now: one note, or every waiting/failed note. */
  async retry(id?: string): Promise<void> {
    const notes = await this.list();
    for (const note of notes) {
      if (id === undefined || note.id === id) await this.store.put({ ...note, status: 'waiting', nextAttemptAt: 0 });
    }
    await this.emit();
    await this.process();
  }

  /** Starts the triggers: 'online', returning to the tab, and once right away (app start). */
  start(): void {
    if (this.detach || typeof window === 'undefined') return;
    const kick = (): void => {
      void this.process();
    };
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') kick();
    };
    window.addEventListener('online', kick);
    document.addEventListener('visibilitychange', onVisible);
    this.detach = () => {
      window.removeEventListener('online', kick);
      document.removeEventListener('visibilitychange', onVisible);
    };
    kick();
  }

  stop(): void {
    this.detach?.();
    this.detach = null;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }

  private schedule(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    void this.list().then((notes) => {
      const due = notes.filter((n) => n.status === 'waiting').map((n) => n.nextAttemptAt);
      if (due.length === 0) return;
      const delay = Math.max(0, Math.min(...due) - this.now());
      this.timer = setTimeout(() => {
        this.timer = null;
        void this.process();
      }, delay);
    });
  }

  /** Uploads every due waiting note, one at a time. Concurrent calls share one run. */
  process(): Promise<void> {
    if (this.processing) return this.processing;
    this.processing = this.runOnce().finally(() => {
      this.processing = null;
      this.schedule();
    });
    return this.processing;
  }

  private async runOnce(): Promise<void> {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    const notes = await this.list();
    for (const note of notes) {
      if (note.status !== 'waiting' || note.nextAttemptAt > this.now()) continue;
      try {
        const capture = await this.upload(note);
        await this.store.remove(note.id);
        await this.emit();
        this.options.onUploaded?.(capture, note);
      } catch (err) {
        const offline = !(err instanceof ApiError);
        const attempts = note.attempts + 1;
        const failed = !offline && (!isTransient(err) || attempts >= (this.options.maxServerAttempts ?? 3));
        await this.store.put({
          ...note,
          attempts,
          lastError: err instanceof ApiError ? err.code : 'NETWORK',
          status: failed ? 'failed' : 'waiting',
          nextAttemptAt: this.now() + backoffMs(attempts),
        });
        await this.emit();
        // No connection: the next notes would fail the same way.
        if (offline) return;
      }
    }
  }
}
